import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { ResendCountdown } from "@/components/auth/check-email/resend-countdown";
import { Button } from "@/components/ui/button/button";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import { useErrorDrawer } from "@/hooks/use-error-drawer";
import { usePasswordReset } from "@/hooks/use-password-reset";
import { passwordResetAtom } from "@/lib/atoms/password-reset-atom";

import type { JSX } from "react";

const CheckEmailPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { email } = useAtomValue(passwordResetAtom);
  const { showErrorDrawer } = useErrorDrawer();
  const { sendResetPasswordEmail } = usePasswordReset();

  const handleResend = async (): Promise<void> => {
    try {
      await sendResetPasswordEmail(email);
    } catch {
      showErrorDrawer("Não foi possível reenviar o e-mail. Tente novamente.");
    }
  };

  const handleBackToLogin = (): void => {
    void navigate({ to: "/auth/signin" });
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={0.66} />
      </FlowHeader>

      <div className="flex flex-col flex-1 justify-between p-6 gap-6">
        <div className="flex flex-col gap-4">
          <div>
            <PageTitle className="pt-0">Verifique seu e-mail</PageTitle>
            <PageDescription>
              Se o e-mail estiver correto, você vai receber uma mensagem com o link para
              redefinir sua senha.
            </PageDescription>
          </div>

          <ResendCountdown initialSeconds={30} onResend={() => void handleResend()} />
        </div>

        <div className="flex flex-col gap-3 w-full">
          <Button
            color="secondaryLight"
            onClick={handleBackToLogin}
            className="w-full text-neutral-600 font-medium bg-neutral-200 hover:bg-neutral-50"
            aria-label="Voltar para login"
          >
            Voltar para login
          </Button>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export const Route = createFileRoute("/auth/password-reset/check-email")({
  component: CheckEmailPage,
});
